import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Link2, Share2, X } from "lucide-react";

import "./ShareModal.css";
import PipAnchor from "./Pip/PipAnchor";

const SHARE_TEXT = "Check this out on 100ViewsOnly — only 100 people get to see it";

function getShareUrl(url) {
  return url || (typeof window !== "undefined" ? window.location.href : "");
}

function getShareMessage(url) {
  return `${SHARE_TEXT} ${url}`;
}

function IconInstagram() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="white" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
    </svg>
  );
}

function IconTikTok() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
      <path d="M16.5 5.5c.8 1.6 2.1 2.8 3.8 3.1v3.2c-1.4 0-2.7-.4-3.8-1.2v5.9c0 3.4-2.8 5.4-5.7 5.4-2.9 0-5.3-2.1-5.3-5.1 0-3.1 2.4-5.1 5.8-5.1.5 0 1 .1 1.5.2v3.3c-.4-.2-.9-.4-1.4-.4-1.4 0-2.4.9-2.4 2.3s1 2.3 2.4 2.3c1.5 0 2.5-1 2.5-2.7V2h3.6c.1.5.2 1.1.2 1.7 0 .6.1 1.1.2 1.8z" />
    </svg>
  );
}

function IconX() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
      <path d="M18.9 2H22l-6.8 7.8L23 22h-6.7l-4.7-6.1L6.6 22H2.5l7.3-8.4L1 2h6.9l4.2 5.5L18.9 2zm-1.2 18h1.8L7.1 3.9H5.2L17.7 20z" />
    </svg>
  );
}

function IconFacebook() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
      <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07c0 6.02 4.39 11.01 10.13 11.91v-8.4H7.08v-3.5h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.23 2.68.23v2.96h-1.51c-1.49 0-1.95.92-1.95 1.87v2.25h3.32l-.53 3.5h-2.79v8.4C19.61 23.08 24 18.09 24 12.07z" />
    </svg>
  );
}

function IconWhatsApp() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function IconSms() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function ShareModal({ isOpen, onClose, url, title }) {
  const [copied, setCopied] = useState(false);
  const [hint, setHint] = useState("");

  const shareUrl = useMemo(() => getShareUrl(url), [url]);
  const canNativeShare = typeof navigator !== "undefined" && Boolean(navigator.share);

  useEffect(() => {
    if (!isOpen) return undefined;
    setCopied(false);
    setHint("");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!hint) return undefined;
    const timer = setTimeout(() => setHint(""), 4000);
    return () => clearTimeout(timer);
  }, [hint]);

  const copyLink = useCallback(async (message) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      if (message) setHint(message);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setHint("Could not copy link — try again.");
    }
  }, [shareUrl]);

  const openWindow = useCallback((href) => {
    window.open(href, "_blank", "noopener,noreferrer");
  }, []);

  if (!isOpen) return null;

  const platforms = [
    {
      id: "instagram",
      label: "Instagram",
      bg: "linear-gradient(135deg, #833AB4, #FD1D1D, #FCAF45)",
      icon: <IconInstagram />,
      action: async () => {
        await copyLink("Link copied — paste in your Instagram caption.");
        openWindow("https://www.instagram.com/");
      },
    },
    {
      id: "tiktok",
      label: "TikTok",
      bg: "#010101",
      icon: <IconTikTok />,
      action: async () => {
        await copyLink("Paste this in your TikTok bio or caption");
      },
    },
    {
      id: "x",
      label: "X",
      bg: "#000000",
      icon: <IconX />,
      action: () => {
        const params = new URLSearchParams({
          text: SHARE_TEXT,
          url: shareUrl,
        });
        openWindow(`https://twitter.com/intent/tweet?${params.toString()}`);
      },
    },
    {
      id: "facebook",
      label: "Facebook",
      bg: "#1877F2",
      icon: <IconFacebook />,
      action: () => {
        openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
      },
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      bg: "#25D366",
      icon: <IconWhatsApp />,
      action: () => {
        openWindow(`https://wa.me/?text=${encodeURIComponent(getShareMessage(shareUrl))}`);
      },
    },
    {
      id: "sms",
      label: "SMS",
      bg: "#34C759",
      icon: <IconSms />,
      action: () => {
        window.location.href = `sms:?&body=${encodeURIComponent(getShareMessage(shareUrl))}`;
      },
    },
  ];

  if (canNativeShare) {
    platforms.push({
      id: "native",
      label: "More",
      bg: "linear-gradient(135deg, #C4790A, #A86208)",
      icon: <Share2 size={18} color="white" strokeWidth={2.2} />,
      action: async () => {
        try {
          await navigator.share({
            title: title || "100ViewsOnly",
            text: SHARE_TEXT,
            url: shareUrl,
          });
        } catch (err) {
          if (err?.name !== "AbortError") {
            setHint("Could not open share sheet.");
          }
        }
      },
    });
  }

  return (
    <div
      className="share-sheet-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="share-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-sheet-title"
      >
        <button type="button" className="share-sheet-close" onClick={onClose} aria-label="Close">
          <X size={16} strokeWidth={2.5} />
        </button>

        <div className="share-sheet-header">
          <h2 id="share-sheet-title" className="share-sheet-title">
            Share this <em>drop</em>
          </h2>
          <p className="share-sheet-sub">Only 100 people get to see it — spread the word.</p>
        </div>

        <div className="share-sheet-inner">
          <button
            type="button"
            className={`share-sheet-copy${copied ? " is-copied" : ""}`}
            onClick={() => copyLink()}
          >
            <span className="share-sheet-copy-icon">
              {copied ? <Check size={16} strokeWidth={2.5} /> : <Link2 size={16} strokeWidth={2.5} />}
            </span>
            <span className="share-sheet-copy-text">
              <strong>{copied ? "Copied!" : "Copy link"}</strong>
              <span className="share-sheet-copy-url">{shareUrl}</span>
            </span>
          </button>

          {hint && <p className="share-sheet-hint">{hint}</p>}

          <div className="share-sheet-grid">
            <PipAnchor id="share-modal">
              <div className="share-sheet-grid-inner">
                {platforms.map((platform) => (
                  <button
                    key={platform.id}
                    type="button"
                    className="share-sheet-platform"
                    onClick={platform.action}
                    aria-label={`Share on ${platform.label}`}
                  >
                    <span className="share-sheet-platform-tooltip" aria-hidden="true">
                      {platform.label}
                    </span>
                    <span
                      className="share-sheet-platform-icon"
                      style={{ background: platform.bg }}
                    >
                      {platform.icon}
                    </span>
                  </button>
                ))}
              </div>
            </PipAnchor>
          </div>
        </div>
      </div>
    </div>
  );
}
